import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { recruitmentLabels, type RecruitmentStatus } from './productionCommissionsModel'

const descriptions: Partial<Record<RecruitmentStatus, string>> = {
  draft: '暂不对用户展示',
  recruiting: '开始日期后可申请',
  paused: '暂停接收新申请',
  ended: '不再接受申请',
}
const statuses: RecruitmentStatus[] = ['draft', 'recruiting', 'paused', 'ended']

export default function RecruitmentStatusSelect({ value, onChange }: { value: RecruitmentStatus; onChange: (value: RecruitmentStatus) => void }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const options = useRef<Partial<Record<RecruitmentStatus, HTMLButtonElement | null>>>({})

  useLayoutEffect(() => {
    if (!open || !menu.current || !trigger.current) return
    const node = menu.current
    node.setAttribute('popover', 'manual')
    node.showPopover?.()
    const position = () => {
      const rect = trigger.current!.getBoundingClientRect()
      node.style.left = `${rect.left}px`
      node.style.width = `${rect.width}px`
      node.style.top = `${rect.bottom + node.offsetHeight + 8 > window.innerHeight ? rect.top - node.offsetHeight - 4 : rect.bottom + 4}px`
    }
    position()
    options.current[value]?.focus()
    window.addEventListener('resize', position)
    window.addEventListener('scroll', position, true)
    return () => {
      window.removeEventListener('resize', position)
      window.removeEventListener('scroll', position, true)
      if (node.matches(':popover-open')) node.hidePopover()
    }
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false) }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape, true)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape, true)
    }
  }, [open])

  return <div ref={root} className="relative min-w-0 text-sm">
    <span id="commission-status-label" className="font-medium">招募状态</span>
    <button ref={trigger} type="button" aria-labelledby="commission-status-label" aria-haspopup="listbox" aria-expanded={open} aria-controls="commission-status-options"
      className="mt-1 flex w-full items-center rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left"
      onClick={() => setOpen(current => !current)} onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true) } }}>
      <strong className="shrink-0 font-medium">{recruitmentLabels[value]}</strong>
      <span className="mx-2 text-slate-400">—</span>
      <span className="min-w-0 flex-1 truncate text-slate-500">{descriptions[value] || '由签约与交付流程更新'}</span>
      <ChevronDown size={16} className={open ? 'ml-2 shrink-0 rotate-180' : 'ml-2 shrink-0'} aria-hidden="true" />
    </button>
    {open && <div ref={menu} id="commission-status-options" role="listbox" aria-labelledby="commission-status-label"
      className="fixed z-[100] overflow-hidden rounded-lg border border-slate-300 bg-white py-1 shadow-lg" style={{ margin: 0, inset: 'auto' }}>
      {statuses.map((status, index) => <button key={status} ref={node => { options.current[status] = node }} type="button" role="option"
        aria-selected={value === status}
        className={'grid w-full grid-cols-[20px_64px_12px_minmax(0,1fr)] items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100 ' + (value === status ? 'bg-slate-100' : '')}
        onClick={() => { onChange(status); setOpen(false); trigger.current?.focus() }}
        onKeyDown={event => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
          event.preventDefault()
          options.current[statuses[(index + (event.key === 'ArrowDown' ? 1 : statuses.length - 1)) % statuses.length]]?.focus()
        }}>
        <span aria-hidden="true">{value === status && <Check size={17} strokeWidth={2.5} />}</span>
        <strong className="font-medium">{recruitmentLabels[status]}</strong>
        <span className="text-slate-400">—</span>
        <span className="truncate text-slate-500">{descriptions[status]}</span>
      </button>)}
    </div>}
  </div>
}
